import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, ChefHat, Star, AlertCircle, Loader, Utensils, Beer, MessageCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import BackButton from '../components/BackButton';
import LoadingIndicator from '../components/LoadingIndicator';
import { useLanguage } from '../contexts/LanguageContext';
import DinnerRecommendationsList from '../components/DinnerRecommendationsList';
import { getAcceptedDinnerRecommendations } from '../lib/dinnerRecommendations';

const MyDinners = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upcomingDinners, setUpcomingDinners] = useState<any[]>([]);
  const [pastDinners, setPastDinners] = useState<any[]>([]);
  const [acceptedRecommendations, setAcceptedRecommendations] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'recommendations'>('upcoming');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }
      
      fetchMyDinners();
      fetchAcceptedRecommendations();
    } catch (err) {
      console.error('Error checking auth:', err);
      setError('Authentication error. Please try logging in again.');
      setLoading(false);
    }
  };

  const fetchMyDinners = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current date
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      // Fetch dinner participants
      const { data: participations, error: participationsError } = await supabase
        .from('dinner_participants')
        .select(`
          id,
          dinner_id,
          dinner:dinners(
            id,
            date,
            time,
            spots_total,
            spots_available,
            price_range,
            age_range,
            preferred_gender,
            dining_style,
            description,
            status,
            restaurant:restaurants(*)
          )
        `)
        .eq('user_id', user.id);

      if (participationsError) throw participationsError;

      // Fetch blind date participants
      const { data: blindDateParticipations, error: blindDateError } = await supabase
        .from('blind_date_participants')
        .select(`
          id,
          blind_date_id,
          blind_date:blind_dates(
            id,
            date,
            time,
            spots_total,
            spots_available,
            price_range,
            age_range,
            preferred_gender,
            dining_style,
            description,
            status,
            restaurant:restaurants(*),
            bar:bars(*)
          )
        `)
        .eq('user_id', user.id);

      if (blindDateError) throw blindDateError;

      // Fetch drinking night participants
      const { data: drinkingNightParticipations, error: drinkingNightError } = await supabase
        .from('drinking_night_participants')
        .select(`
          id,
          drinking_night_id,
          drinking_night:drinking_nights(
            id,
            date,
            time,
            spots_total,
            spots_available,
            price_range,
            age_range,
            preferred_gender,
            style,
            description,
            status,
            bar:bars(*)
          )
        `)
        .eq('user_id', user.id);

      if (drinkingNightError) throw drinkingNightError;

      // Process and combine all events
      const allEvents = [
        // Process regular dinners
        ...(participations || [])
          .filter(p => p.dinner)
          .map(p => ({
            ...p.dinner,
            type: 'dinner',
            participation_id: p.id,
            venue: p.dinner.restaurant
          })),
        
        // Process blind dates
        ...(blindDateParticipations || [])
          .filter(p => p.blind_date)
          .map(p => ({
            ...p.blind_date,
            type: 'blind_date',
            participation_id: p.id,
            venue: p.blind_date.restaurant || p.blind_date.bar
          })),
        
        // Process drinking nights
        ...(drinkingNightParticipations || [])
          .filter(p => p.drinking_night)
          .map(p => ({
            ...p.drinking_night,
            type: 'drinking_night',
            participation_id: p.id,
            venue: p.drinking_night.bar,
            dining_style: p.drinking_night.style // Normalize field name
          }))
      ];

      // Split into upcoming and past events
      const upcoming = allEvents.filter(event => {
        if (!event.date) return false;
        return event.date >= todayStr;
      }).sort((a, b) => {
        // Sort by date and time
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return (a.time || '').localeCompare(b.time || '');
      });

      const past = allEvents.filter(event => {
        if (!event.date) return false;
        return event.date < todayStr;
      }).sort((a, b) => {
        // Sort by date and time in reverse (most recent first)
        if (a.date !== b.date) return b.date.localeCompare(a.date);
        return (b.time || '').localeCompare(a.time || '');
      });

      setUpcomingDinners(upcoming);
      setPastDinners(past);
    } catch (err) {
      console.error('Error fetching dinners:', err);
      setError(err instanceof Error ? err.message : 'Failed to load your dinners');
    } finally {
      setLoading(false);
    }
  };

  const fetchAcceptedRecommendations = async () => {
    try {
      const recommendations = await getAcceptedDinnerRecommendations();
      
      // Process recommendations to match the expected format
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const processedRecommendations = recommendations.map(rec => {
        const isSent = rec.sender_id === user.id;
        return {
          id: rec.id,
          type: isSent ? 'sent' : 'received',
          status: rec.status,
          date: rec.date,
          time: rec.time,
          message: rec.message,
          created_at: rec.created_at,
          with_user: isSent ? rec.receiver : rec.sender,
          venue: rec.restaurant || rec.bar
        };
      });
      
      setAcceptedRecommendations(processedRecommendations);
    } catch (err) {
      console.error('Error fetching accepted recommendations:', err);
      // Don't set error state here to avoid blocking the main dinner display
    }
  };

  const formatEventType = (type: string) => {
    switch (type) {
      case 'dinner': return t('myDinners.dinner');
      case 'blind_date': return t('myDinners.blindDate');
      case 'drinking_night': return t('myDinners.drinkingNight');
      case 'recommendation': return t('myDinners.dinnerWithFriend');
      default: return type;
    }
  };

  if (loading) {
    return <LoadingIndicator message={t('common.loading')} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const displayedDinners = activeTab === 'upcoming' ? upcomingDinners : pastDinners;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <BackButton />
        <div className="text-center flex-1">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('myDinners.title')}</h1>
          <p className="text-xl text-gray-600">
            {t('myDinners.subtitle')}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-8">
        <button
          className={`px-6 py-3 font-medium text-lg ${
            activeTab === 'upcoming'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('upcoming')}
        >
          {t('myDinners.upcoming')}
        </button>
        <button
          className={`px-6 py-3 font-medium text-lg ${
            activeTab === 'past'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('past')}
        >
          {t('myDinners.past')}
        </button>
        <button
          className={`px-6 py-3 font-medium text-lg flex items-center ${
            activeTab === 'recommendations'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('recommendations')}
        >
          <MessageCircle className="h-5 w-5 mr-2" />
          {t('myDinners.recommendations')}
          {acceptedRecommendations.length > 0 && (
            <span className="ml-2 bg-orange-100 text-orange-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
              {acceptedRecommendations.length}
            </span>
          )}
        </button>
      </div>

      {/* Recommendations Tab */}
      {activeTab === 'recommendations' && (
        <>
          {acceptedRecommendations.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('myDinners.noRecommendations')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('myDinners.recommendationsMessage')}
              </p>
            </div>
          ) : (
            <DinnerRecommendationsList recommendations={acceptedRecommendations} />
          )}
        </>
      )}

      {/* Dinners Grid */}
      {(activeTab === 'upcoming' || activeTab === 'past') && (
        <>
          {displayedDinners.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <Utensils className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {activeTab === 'upcoming' ? t('myDinners.noUpcoming') : t('myDinners.noPast')}
              </h3>
              <p className="text-gray-600 mb-4">
                {activeTab === 'upcoming'
                  ? t('myDinners.upcomingMessage')
                  : t('myDinners.pastMessage')}
              </p>
              <Link
                to="/search"
                className="inline-block bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600 transition"
              >
                {t('myDinners.findDinners')}
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {displayedDinners.map((dinner) => (
                <div key={`${dinner.type}-${dinner.id}`} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {dinner.venue?.pictures?.exterior?.[0] ? (
                    <img
                      src={dinner.venue.pictures.exterior[0]}
                      alt={dinner.venue.name}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        console.error(`Failed to load image: ${dinner.venue.pictures.exterior[0]}`);
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop';
                      }}
                    />
                  ) : (
                    <div className="h-48 bg-gray-200 flex items-center justify-center">
                      {dinner.type === 'drinking_night' ? (
                        <Beer className="h-12 w-12 text-gray-400" />
                      ) : (
                        <ChefHat className="h-12 w-12 text-gray-400" />
                      )}
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{dinner.venue?.name}</h3>
                        <div className="flex items-center space-x-2 text-gray-600 mt-1">
                          {dinner.type === 'drinking_night' || (dinner.type === 'blind_date' && dinner.bar) ? (
                            <Beer className="h-4 w-4" />
                          ) : (
                            <ChefHat className="h-4 w-4" />
                          )}
                          <span>
                            {dinner.venue?.cuisine_type || dinner.venue?.bar_type || 'Dining Experience'}
                          </span>
                          {dinner.price_range && (
                            <>
                              <span>•</span>
                              <span>{dinner.price_range}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-sm">
                        {formatEventType(dinner.type)}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>{dinner.date && new Date(dinner.date).toLocaleDateString()}</span>
                        {dinner.time && (
                          <>
                            <span>•</span>
                            <Clock className="h-4 w-4" />
                            <span>{dinner.time.slice(0, 5)}</span>
                          </>
                        )}
                      </div>

                      {dinner.venue?.address && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <MapPin className="h-4 w-4" />
                          <span>{dinner.venue.address}</span>
                        </div>
                      )}

                      {dinner.with_user && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Users className="h-4 w-4" />
                          <span>{t('myDinners.with')} {dinner.with_user.full_name}</span>
                        </div>
                      )}

                      {dinner.dining_style && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Utensils className="h-4 w-4" />
                          <span>{dinner.dining_style}</span>
                        </div>
                      )}
                    </div>

                    {dinner.description && (
                      <p className="text-gray-600 mb-4 line-clamp-2">{dinner.description}</p>
                    )}

                    {activeTab === 'upcoming' && dinner.venue?.address && (
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(dinner.venue.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition text-center block"
                      >
                        {t('myDinners.getDirections')}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyDinners;